/**
 * @api.ts
 * Provides methods through which to make requests to the API.
 */
import { toast } from 'react-toastify';
import 'url-polyfill';
import 'whatwg-fetch';
import { castle } from '@ReactComponents/../utils/castle';
import { INCOMPLETE_COMPANY, INCOMPLETE_USER } from '@Shared/constants';
const request = (url, options = {}) => {
    options.credentials = 'same-origin';
    if (castle) {
        castle.createRequestToken().then((requestToken) => {
            options.headers['X-Castle-Request-Token'] = requestToken;
        });
    }
    return fetch(url, options).then((response) => {
        if (response.ok) {
            return Promise.resolve(response);
        }
        return response.text().then((text) => {
            switch (text) {
                case INCOMPLETE_USER:
                    window.location.href = '/welcomeUser';
                    break;
                case INCOMPLETE_COMPANY:
                    window.location.href = '/welcomeCompany';
                    break;
            }
            throw new Error(text || response.statusText);
        });
    });
};
export class Api {
    static get(urlString, includes) {
        const url = new URL(urlString, window.location.origin);
        if (includes) {
            includes.forEach((inclusion) => url.searchParams.append(`include[${inclusion}]`, ''));
        }
        const companyId = Api.getCompanyId();
        return request(url.toString(), {
            headers: {
                companyid: companyId
            }
        });
    }
    static constructRequest(method, url, body, injectedOptions) {
        const companyId = Api.getCompanyId();
        const headers = {
            'X-CSRF-Token': window.CSRF,
            companyId: companyId
        };
        const options = Object.assign({ method: method, headers: headers }, injectedOptions);
        if (body) {
            options.body = JSON.stringify(body);
            options.headers['Content-Type'] = 'application/json; charset=utf-8';
            options.headers.Accept = 'application/json, text/plain, */*';
        }
        return request(url, options);
    }
    static responseToJSON(response) {
        return response.then((raw) => raw.json());
    }
    static responseToJSONAndToastError(response) {
        return Api.responseToJSON(response).catch((error) => {
            try {
                const data = JSON.parse(error.message);
                toast.error(data.error);
            }
            catch (_a) {
                toast.error(error.message);
            }
            throw new Error(error.message);
        });
    }
    static responseWithToastError(response) {
        return response.catch((error) => {
            try {
                const data = JSON.parse(error.message);
                toast.error(data.error);
            }
            catch (_a) {
                toast.error(error.message);
            }
            throw new Error(error.message);
        });
    }
    static toastError(error) {
        try {
            const data = JSON.parse(error.message);
            toast.error(data.error);
        }
        catch (_a) {
            toast.error(error.message);
        }
        throw new Error(error.message);
    }
    static put(url, body, options) {
        return Api.constructRequest('PUT', url, body, options);
    }
    static post(url, body, options) {
        return Api.constructRequest('POST', url, body, options);
    }
    static postFile(url, body) {
        const companyId = Api.getCompanyId();
        const headers = {
            'X-CSRF-Token': window.CSRF,
            companyId: companyId
        };
        const options = {
            method: 'POST',
            headers: headers,
            body: body
        };
        return request(url, options);
    }
    static patch(url, body, options) {
        return Api.constructRequest('PATCH', url, body, options);
    }
    static delete(url, body, options) {
        return Api.constructRequest('DELETE', url, body, options);
    }
    static parseFilters(params) {
        return Object.keys(params)
            .map((field) => {
            const value = params[field];
            if (value === undefined || (value && value.length === 0)) {
                return;
            }
            // value can be: null, number, non-empty arrays, non-empty strings, or any objects (empty or not)
            return {
                field,
                value
            };
        })
            .filter(Boolean);
    }
}
Api.getCompanyId = () => {
    if (window.companyId) {
        return window.companyId;
    }
    else if (window.ORG) {
        return window.ORG.id;
    }
    return '';
};
